import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, RefreshCw, Copy, Check, Sparkles, AlertCircle, Bot, User, Trash2 } from 'lucide-react';
import { sendWorkerAiRequest, parseNutritionReply, parseRehabReply, buildNutritionPrompt, ChatMessage } from '../lib/workerAi';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import VoiceInputButton from './VoiceInputButton';

interface AiChatBoxProps {
  feature: 'rehabilitation' | 'nutrition';
  title: string;
  subtitle?: string;
  placeholder?: string;
  initialContext?: string;
  onApplyResult?: (parsedData: any) => void;
  applyButtonText?: string;
}

export default function AiChatBox({
  feature,
  title,
  subtitle,
  placeholder = 'پیام خود را بنویسید...',
  initialContext,
  onApplyResult,
  applyButtonText = 'استفاده از این نتیجه در برنامه',
}: AiChatBoxProps) {
  const getWelcomeMessage = (): ChatMessage => ({
    id: 'welcome',
    role: 'assistant',
    content:
      feature === 'rehabilitation'
        ? 'سلام! من دستیار هوشمند فیزیوتراپی و توانبخشی هستم. لطفاً وضعیت درد، محل آسیب یا محدودیت حرکتی خود را شرح دهید تا برنامه تمرینی تخصصی به شما پیشنهاد دهم.'
        : 'سلام! من دستیار هوشمند تغذیه هستم. توضیحات وعده غذایی خود (مانند "۱۵۰ گرم مرغ گریل شده و یک بشقاب برنج") را ارسال کنید تا کالری و درشت‌مغذی‌ها را محاسبه کنم.',
    timestamp: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }),
  });

  const [messages, setMessages] = useState<ChatMessage[]>(() => [getWelcomeMessage()]);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingRef = useRef(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Load persistent chat history from Cloudflare D1
  useEffect(() => {
    let isMounted = true;
    apiGet('/api/ai/chat/history', { feature })
      .then((res) => {
        if (!isMounted) return;
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          const loadedMessages: ChatMessage[] = res.data.map((m: any) => ({
            id: m.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
            role: m.role as 'user' | 'assistant',
            content: m.content,
            timestamp: m.timestamp || new Date(m.created_at).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }),
          }));
          setMessages(loadedMessages);
        } else {
          setMessages([getWelcomeMessage()]);
        }
      })
      .catch((err) => {
        console.warn('Failed loading D1 chat history:', err);
      });

    return () => {
      isMounted = false;
    };
  }, [feature]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend || input).trim();
    if (!text || isLoading || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setErrorMsg(null);
    const userMessageText = initialContext ? `${initialContext}\n\nپیام کاربر: ${text}` : text;

    const userMessage: ChatMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      role: 'user',
      content: text,
      timestamp: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    // Persist user message to Cloudflare D1
    apiPost('/api/ai/chat/history', {
      id: userMessage.id,
      feature,
      role: 'user',
      content: userMessage.content,
      timestamp: userMessage.timestamp,
    }).catch((err) => console.warn('Failed saving user chat message to D1:', err));

    // Format history for worker (exclude the current user message being sent, keep last 3-4 entries max)
    const history = updatedMessages
      .filter((m) => m.id !== 'welcome')
      .slice(0, -1)
      .slice(-4)
      .map((m) => ({ role: m.role, content: m.content.slice(0, 400) }));

    try {
      const messageForAi = feature === 'nutrition' ? buildNutritionPrompt(userMessageText) : userMessageText;
      const replyText = await sendWorkerAiRequest(feature, messageForAi, history);

      const assistantMessage: ChatMessage = {
        id: `msg_${Date.now() + 1}_${Math.random().toString(36).substr(2, 4)}`,
        role: 'assistant',
        content: replyText,
        timestamp: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Persist assistant message to Cloudflare D1
      apiPost('/api/ai/chat/history', {
        id: assistantMessage.id,
        feature,
        role: 'assistant',
        content: assistantMessage.content,
        timestamp: assistantMessage.timestamp,
      }).catch((err) => console.warn('Failed saving assistant chat message to D1:', err));
    } catch (err: any) {
      console.error('Chat error:', err);
      setErrorMsg(err.message || 'خطا در برقراری ارتباط با هوش مصنوعی');
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleRetry = () => {
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUserMsg) {
      handleSendMessage(lastUserMsg.content);
    }
  };

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleClearHistory = () => {
    if (confirm('آیا از پاک کردن تاریخچه گفتگو اطمینان دارید؟')) {
      apiDelete('/api/ai/chat/history', { feature }).catch((err) =>
        console.warn('Failed clearing chat history from D1:', err)
      );
      setMessages([getWelcomeMessage()]);
    }
  };

  return (
    <div className="premium-card p-4 md:p-6 dark:bg-gray-800 border-gray-100 dark:border-gray-800 flex flex-col h-[520px] shadow-sm relative overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center border-b pb-3.5 mb-3 border-gray-100 dark:border-gray-700/60 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="p-2 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 rounded-xl">
            <Sparkles className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white font-display flex items-center gap-1.5">
              <span>{title}</span>
              <span className="text-[9px] px-2 py-0.5 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 font-mono rounded-full font-bold">
                هوش مصنوعی آنلاین
              </span>
            </h3>
            {subtitle && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>

        <button
          onClick={handleClearHistory}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
          title="پاک‌سازی تاریخچه"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Messages Scroll Area */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto space-y-4 pr-1 pl-1 py-2 scroll-smooth text-xs"
        dir="rtl"
      >
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-2.5 items-start ${
              msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'
            }`}
          >
            {/* Avatar */}
            <div
              className={`w-7 h-7 rounded-xl flex items-center justify-center shrink-0 text-white shadow-xs ${
                msg.role === 'user'
                  ? 'bg-indigo-600'
                  : 'bg-emerald-600'
              }`}
            >
              {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
            </div>

            {/* Bubble */}
            <div
              className={`max-w-[92%] sm:max-w-[88%] rounded-2xl p-3.5 space-y-2 relative group shadow-2xs ${
                msg.role === 'user'
                  ? 'bg-indigo-600 text-white rounded-tr-none'
                  : 'bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-100 rounded-tl-none'
              }`}
            >
              {/* Content rendering */}
              <div className="leading-relaxed font-sans break-words">
                {msg.role === 'assistant' ? (
                  <div className="markdown-body text-xs leading-relaxed">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        table: ({ children }) => (
                          <div className="overflow-x-auto my-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-2xs">
                            <table className="w-full text-right text-xs border-collapse min-w-[280px]">
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({ children }) => (
                          <thead className="bg-emerald-50 dark:bg-emerald-950/60 text-emerald-900 dark:text-emerald-200 border-b border-gray-200 dark:border-gray-700 font-bold text-[11px]">
                            {children}
                          </thead>
                        ),
                        tbody: ({ children }) => (
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800/80 bg-white dark:bg-gray-900">
                            {children}
                          </tbody>
                        ),
                        tr: ({ children }) => (
                          <tr className="hover:bg-slate-50/80 dark:hover:bg-gray-800/40 transition-colors">
                            {children}
                          </tr>
                        ),
                        th: ({ children }) => (
                          <th className="py-2 px-3 font-bold text-right text-[11px] whitespace-nowrap text-emerald-950 dark:text-emerald-300">
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td className="py-2 px-3 text-[11px] text-gray-700 dark:text-gray-200 font-sans border-t border-gray-100 dark:border-gray-800">
                            {children}
                          </td>
                        ),
                        p: ({ children }) => (
                          <p className="mb-2 leading-relaxed text-gray-800 dark:text-gray-100 last:mb-0">
                            {children}
                          </p>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc list-inside space-y-1 my-2 text-xs text-gray-700 dark:text-gray-200">
                            {children}
                          </ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal list-inside space-y-1 my-2 text-xs text-gray-700 dark:text-gray-200">
                            {children}
                          </ol>
                        ),
                        li: ({ children }) => (
                          <li className="leading-relaxed">
                            {children}
                          </li>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-bold text-emerald-700 dark:text-emerald-400">
                            {children}
                          </strong>
                        ),
                        h1: ({ children }) => <h1 className="text-sm font-bold text-gray-900 dark:text-white my-2 border-b pb-1">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-xs font-bold text-gray-900 dark:text-white my-2">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-xs font-bold text-emerald-600 dark:text-emerald-400 my-1.5">{children}</h3>,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <span className="whitespace-pre-wrap">{msg.content}</span>
                )}
              </div>

              {/* Timestamp and action bar */}
              <div
                className={`flex items-center justify-between pt-1 border-t text-[10px] ${
                  msg.role === 'user'
                    ? 'border-indigo-500/40 text-indigo-100'
                    : 'border-gray-100 dark:border-gray-800 text-gray-400'
                }`}
              >
                <span className="font-mono">{msg.timestamp}</span>

                <div className="flex items-center gap-1.5 opacity-90 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleCopy(msg.id, msg.content)}
                    className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded transition-colors"
                    title="کپی پیام"
                  >
                    {copiedId === msg.id ? (
                      <Check className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </button>

                  {/* If assistant and onApplyResult provided */}
                  {msg.role === 'assistant' && msg.id !== 'welcome' && onApplyResult && (
                    <button
                      onClick={() => {
                        const prevUserMsg = messages
                          .slice(0, messages.findIndex((m) => m.id === msg.id))
                          .reverse()
                          .find((m) => m.role === 'user');
                        const userText = prevUserMsg ? prevUserMsg.content : '';

                        if (feature === 'nutrition') {
                          onApplyResult(parseNutritionReply(msg.content, userText));
                        } else {
                          onApplyResult(parseRehabReply(msg.content, userText));
                        }
                      }}
                      className="px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold text-[9px] transition-all flex items-center gap-1"
                    >
                      <Sparkles className="w-2.5 h-2.5" />
                      {applyButtonText}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {isLoading && (
          <div className="flex gap-2.5 items-start">
            <div className="w-7 h-7 rounded-xl bg-emerald-600 flex items-center justify-center text-white shadow-xs">
              <Bot className="w-4 h-4 animate-bounce" />
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-3.5 rounded-2xl rounded-tl-none flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                هوش مصنوعی در حال تحلیل پاسخ...
              </span>
            </div>
          </div>
        )}

        {/* Error message card */}
        {errorMsg && (
          <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 rounded-xl flex items-center justify-between text-xs text-red-700 dark:text-red-300">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
              <span>{errorMsg}</span>
            </div>
            <button
              onClick={handleRetry}
              className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold text-[10px] flex items-center gap-1 shrink-0"
            >
              <RefreshCw className="w-3 h-3" />
              تلاش مجدد
            </button>
          </div>
        )}
      </div>

      {/* Input controls */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
        className="pt-3 border-t border-gray-100 dark:border-gray-800 flex gap-2 shrink-0"
        dir="rtl"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          disabled={isLoading}
          className="flex-1 text-xs px-3.5 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />

        <VoiceInputButton
          currentValue={input}
          onUpdate={(val) => setInput(val)}
          disabled={isLoading}
          title="ورودی صوتی"
        />

        <button
          type="submit"
          disabled={isLoading || !input.trim()}
          className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shrink-0 shadow-xs cursor-pointer"
        >
          {isLoading ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4 rotate-180" />
              ارسال
            </>
          )}
        </button>
      </form>
    </div>
  );
}
